import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { invalidateMany } from './queryUtils';
import {
  fetchProjectNeeds,
  upsertProjectNeeds,
  fetchProjectAssignments,
  createCapacityAssignment,
  updateCapacityAssignment,
  deleteCapacityAssignment,
  fetchAvailablePeople,
  fetchProjectOverview,
  fetchPlanningMatrix,
  fetchHiringGaps,
  fetchProjectsCapacitySummary,
  shiftProjectPlan,
  fetchRoleTagMatrix,
  toggleRoleTag,
  fetchUserRoleTags,
  setUserRoleTags,
  fillCapacityNeed,
  confirmProjectAssignment,
} from '../services/api/capacity';
import type { FillCapacityNeedInput } from '../services/api/capacity';

// ===== Capacity Needs =====

export function useProjectNeeds(projectId: number | undefined) {
  return useQuery({
    queryKey: ['capacityNeeds', projectId],
    queryFn: () => fetchProjectNeeds(projectId!),
    enabled: !!projectId,
  });
}

export function useUpsertNeeds() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, needs }: { projectId: number; needs: { roleId: number; month: string; fte: number }[] }) =>
      upsertProjectNeeds(projectId, needs),
    onSuccess: (_d, { projectId }) => {
      qc.invalidateQueries({ queryKey: ['capacityNeeds', projectId] });
      qc.invalidateQueries({ queryKey: ['capacityOverview', projectId] });
      qc.invalidateQueries({ queryKey: ['projectsCapacitySummary'] });
      qc.invalidateQueries({ queryKey: ['hiringGaps'] });
      // F9-005: planningMatrix aggregates project_capacity_needs per role
      // and month; needs-upsert mutates the same table, so the matrix must
      // refresh to match every other capacity mutation in this hook file.
      qc.invalidateQueries({ queryKey: ['planningMatrix'] });
    },
  });
}

// ===== Capacity Assignments =====

/**
 * Caches every assignment mutation must refresh: the per-project assignment
 * list, the per-project overview, the planning matrix, the projects-wide
 * summary, and the role/month available-people picker. `hiringGaps` is added
 * by create/delete (which change role-month totals) but not by update (which
 * only shifts FTE within an existing row). Order matches the prior inline
 * sequence — `hiringGaps` slots between `planningMatrix` and
 * `projectsCapacitySummary` — so query-cache observers see invalidations in
 * the same order.
 */
function invalidateAssignmentCaches(qc: QueryClient, opts: { includeHiringGaps: boolean }): void {
  invalidateMany(qc, [
    ['capacityAssignments'],
    ['capacityOverview'],
    ['planningMatrix'],
    ...(opts.includeHiringGaps ? [['hiringGaps'] as const] : []),
    ['projectsCapacitySummary'],
    ['availablePeople'],
  ]);
}

export function useProjectAssignments(projectId: number | undefined) {
  return useQuery({
    queryKey: ['capacityAssignments', projectId],
    queryFn: () => fetchProjectAssignments(projectId!),
    enabled: !!projectId,
  });
}

export function useCreateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, ...data }: { projectId: number; userId: number; roleId: number; month: string; fte: number }) =>
      createCapacityAssignment(projectId, data),
    onSuccess: () => {
      invalidateAssignmentCaches(qc, { includeHiringGaps: true });
    },
  });
}

export function useUpdateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fte }: { id: number; fte: number }) => updateCapacityAssignment(id, fte),
    onSuccess: () => {
      invalidateAssignmentCaches(qc, { includeHiringGaps: false });
    },
  });
}

export function useDeleteAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteCapacityAssignment(id),
    onSuccess: () => {
      invalidateAssignmentCaches(qc, { includeHiringGaps: true });
    },
  });
}

/**
 * MKS-358 — fill a role-month gap through the project assignment, the write
 * the planner uses once `mks_358_planner_allocation_ui` is on. It changes the
 * same role-month totals a capacity-assignment create does, so it refreshes
 * the same caches — `planningMatrix` among them, which is what redraws the
 * person's cell with the new per cent and hours.
 */
export function useFillCapacityNeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, ...input }: { projectId: number } & FillCapacityNeedInput) =>
      fillCapacityNeed(projectId, input),
    onSuccess: () => {
      invalidateAssignmentCaches(qc, { includeHiringGaps: true });
    },
  });
}

/**
 * MKS-358 — one-click confirm of a tentative assignment from the planner.
 * Confirming moves allocation from the tentative total to the confirmed one
 * (and grants the project access a tentative assignment withholds), so every
 * capacity surface has to be re-read; role-month totals do not change, so
 * `hiringGaps` stays out, matching `useUpdateAssignment`.
 */
export function useConfirmAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, assignmentId }: { projectId: number; assignmentId: number }) =>
      confirmProjectAssignment(projectId, assignmentId),
    onSuccess: () => {
      invalidateAssignmentCaches(qc, { includeHiringGaps: false });
    },
  });
}

export function useShiftProjectPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, direction }: { projectId: number; direction: 1 | -1 }) =>
      shiftProjectPlan(projectId, direction),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capacityNeeds'] });
      qc.invalidateQueries({ queryKey: ['capacityAssignments'] });
      qc.invalidateQueries({ queryKey: ['capacityOverview'] });
      qc.invalidateQueries({ queryKey: ['planningMatrix'] });
      qc.invalidateQueries({ queryKey: ['hiringGaps'] });
      qc.invalidateQueries({ queryKey: ['projectsCapacitySummary'] });
    },
  });
}

// ===== Available People =====

export function useAvailablePeople(roleId: number | null, month: string | null, includeAll = false) {
  return useQuery({
    queryKey: ['availablePeople', roleId, month, includeAll],
    queryFn: () => fetchAvailablePeople(roleId!, month!, includeAll),
    enabled: !!roleId && !!month,
  });
}

// ===== Overview =====

export function useProjectOverview(projectId: number | undefined) {
  return useQuery({
    queryKey: ['capacityOverview', projectId],
    queryFn: () => fetchProjectOverview(projectId!),
    enabled: !!projectId,
  });
}

// ===== Projects Capacity Summary =====

export function useProjectsCapacitySummary(fromMonth?: string, toMonth?: string) {
  return useQuery({
    queryKey: ['projectsCapacitySummary', fromMonth, toMonth],
    queryFn: () => fetchProjectsCapacitySummary(fromMonth, toMonth),
  });
}

// ===== Planning Matrix =====

export function usePlanningMatrix(fromMonth?: string, toMonth?: string) {
  return useQuery({
    queryKey: ['planningMatrix', fromMonth, toMonth],
    queryFn: () => fetchPlanningMatrix(fromMonth, toMonth),
  });
}

// ===== Hiring Gaps =====

export function useHiringGaps(fromMonth?: string, toMonth?: string) {
  return useQuery({
    queryKey: ['hiringGaps', fromMonth, toMonth],
    queryFn: () => fetchHiringGaps(fromMonth, toMonth),
  });
}

// ===== User Role Tags =====

// F5-007: role-tag changes affect every query that displays role-eligibility
// for a user — Available People dropdowns, the Planning Matrix, capacity
// assignments (which carry `isRoleMismatch`), and the Hiring Gaps view.
// Previously the toggle/set mutations only invalidated the role-tag tables
// themselves, leaving downstream views rendering pre-tag state.
function invalidateCapacityListsForRoleTags(qc: QueryClient) {
  invalidateMany(qc, [['availablePeople'], ['planningMatrix'], ['capacityAssignments'], ['hiringGaps']]);
}

export function useRoleTagMatrix() {
  return useQuery({
    queryKey: ['roleTagMatrix'],
    queryFn: fetchRoleTagMatrix,
  });
}

export function useToggleRoleTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleId }: { userId: number; roleId: number }) => toggleRoleTag(userId, roleId),
    // F-1407: apply optimistic update to roleTagMatrix cache so the checkbox
    // visually flips before the server responds. UserRoleMatrixPage was
    // previously deriving tagSet from query data and mutating it directly
    // (Set.add / Set.delete on the same object reference), which does NOT
    // trigger a React re-render. Proper fix: write the optimistic state into
    // the TanStack Query cache so React sees a new object reference.
    onMutate: async ({ userId, roleId }) => {
      // Cancel any in-flight refetches that would overwrite our optimistic data.
      await qc.cancelQueries({ queryKey: ['roleTagMatrix'] });

      // Snapshot the previous value so we can roll back on error.
      const previousMatrix = qc.getQueryData<{ data: { tags: { userId: number; roleId: number }[] } }>(['roleTagMatrix']);

      qc.setQueryData<{ data: { tags: { userId: number; roleId: number }[] } }>(
        ['roleTagMatrix'],
        (old) => {
          if (!old) return old;
          const tags = old.data.tags;
          const exists = tags.some((t) => t.userId === userId && t.roleId === roleId);
          const newTags = exists
            ? tags.filter((t) => !(t.userId === userId && t.roleId === roleId))
            : [...tags, { userId, roleId }];
          return { ...old, data: { ...old.data, tags: newTags } };
        },
      );

      return { previousMatrix };
    },
    onError: (_err, _vars, context) => {
      // Roll back to the snapshot on error.
      if (context?.previousMatrix !== undefined) {
        qc.setQueryData(['roleTagMatrix'], context.previousMatrix);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roleTagMatrix'] });
      qc.invalidateQueries({ queryKey: ['userRoleTags'] });
      invalidateCapacityListsForRoleTags(qc);
    },
  });
}

export function useUserRoleTags(userId: number | undefined) {
  return useQuery({
    queryKey: ['userRoleTags', userId],
    queryFn: () => fetchUserRoleTags(userId!),
    enabled: !!userId,
  });
}

export function useSetUserRoleTags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleIds }: { userId: number; roleIds: number[] }) => setUserRoleTags(userId, roleIds),
    onSuccess: (_d, { userId }) => {
      qc.invalidateQueries({ queryKey: ['userRoleTags', userId] });
      qc.invalidateQueries({ queryKey: ['roleTagMatrix'] });
      invalidateCapacityListsForRoleTags(qc);
    },
  });
}
